import { useCallback, useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';

import { Job, OutletContext, PlatformApp } from '@typings';
import {
  getPlatformAppInstnceParams,
  getPlatformAppsInstance,
} from '@services/platformApps.services';
import { contextNamesSelector } from '@selectors';
import { useSelector } from '@hooks';
import { as, invariant, path, toastifyResponseError } from '@utils';

import * as Page from '@pages';
import { ChaseSpinner } from '@components';
import { Layout, PlatformAppManagementLayout } from '@components/Layouts';

type State = {
  instance: PlatformApp.Instance | null;
  input: OutletContext.PlatformAppsInstance['input'];
  output: OutletContext.PlatformAppsInstance['output'];
};
export const PlatformAppManagementRoutes = () => {
  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);

  const params = useParams();

  const [{ instance, input, output }, setAppState] = useImmer<State>({
    instance: null,
    input: null,
    output: null,
  });

  const instanceId = params.instanceId!;
  const { name } = as.o<PlatformApp.Instance>(instance);

  const getAppParamsByType = useCallback(
    async (type: 'input' | 'output') => {
      try {
        invariant(clusterName);
        invariant(projectName);

        const params = await getPlatformAppInstnceParams({
          instanceId,
          type,
          clusterName,
          projectName,
          organizationName,
        });

        setAppState((state) => {
          state[type] = params;
        });
      } catch (error) {
        type PlainError = {
          message?: string;
          detail?: { message?: string };
        };
        const responseError = error as PlainError;

        if (responseError?.detail?.message) {
          setAppState((state) => {
            state[type] = responseError.detail?.message as string;
          });
        }
      }
    },
    [instanceId, organizationName, clusterName, projectName, setAppState],
  );

  const fetchInstanceParams = useCallback(() => {
    getAppParamsByType('input');
    getAppParamsByType('output');
  }, [getAppParamsByType]);

  const fetchInstance: OutletContext.PlatformAppsInstance['getInstance'] =
    useCallback(async () => {
      try {
        invariant(instanceId);

        const instance = await getPlatformAppsInstance({ instanceId });

        setAppState((state) => {
          state.instance = instance;
        });

        return instance;
      } catch (error) {
        toastifyResponseError(error);

        throw error;
      }
    }, [instanceId, setAppState]);

  useEffect(() => {
    const getInstance = async () => {
      try {
        await fetchInstance();
      } catch (error) {
        toastifyResponseError(error);
      }
    };

    getInstance();
    fetchInstanceParams();
  }, [fetchInstanceParams, fetchInstance]);

  if (!instance) {
    return (
      <Layout title={name}>
        <Layout.Content className="relative flex justify-center">
          <ChaseSpinner color="black" className="absolute mt-40 h-14 w-14" />
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <Routes>
      <Route
        element={
          <PlatformAppManagementLayout
            input={input}
            output={output}
            instance={instance}
            getInstance={fetchInstance}
            getInstanceParams={fetchInstanceParams}
          />
        }
      >
        <Route index element={<Page.PlatformAppsInstanceOverview />} />
        <Route path="details" element={<Page.PlatformAppsInstanceOverview />} />
        <Route path="log" element={<Page.PlatformAppsInstanceLog />} />
        <Route
          path="*"
          element={
            <Navigate
              replace
              to={path.appsInstance(instanceId, Job.Tab.Overview)}
            />
          }
        />
      </Route>
    </Routes>
  );
};
